import React, { useEffect, useReducer, useRef, useState } from "react";
import Select from "react-select";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import PageTitle from "../../layouts/PageTitle";
import Swal from "sweetalert2";
import {
  addUniversity,
  getUniversityById,
  updateUniversity,
} from "../../../services/UniversityService";
import { Button } from "react-bootstrap";

const statusOptions = [
  { value: "public", label: "Public" },
  { value: "private", label: "Private" },
];

const AddUniversity = () => {
  const [loading, setLoading] = useState(false);
  const [universityName, setUniversityName] = useState("");
  const [universityType, setUniversityType] = useState({});
  const hasFetched = useRef(false);
  const { id } = useParams();
  const [errors, setErrors] = useState({});

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (id && !hasFetched.current) {
      hasFetched.current = true;
      const fetchUniversity = async () => {
        try {
          setLoading(true);
          const response = await getUniversityById(id);
          const university = response?.data;
          setUniversityName(university?.data?.title);
          let type =
            statusOptions.find(
              (opt) => opt.value === university?.data?.type
            ) || null;
          setUniversityType(type);
        } catch (err) {
          console.error("Error fetching university:", err);
        } finally {
          setLoading(false);
        }
      };
      fetchUniversity();
    }
  }, [id]);

  const validateForm = () => {
    const newErrors = {};

    if (!universityName.trim()) {
      newErrors.universityName = "Title is required";
    }

    if (!universityType) {
      newErrors.type = "University type is required";
    }

    setErrors(newErrors);

    // Return true if no errors
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    try {
      // --- VALIDATE FORMS ---
      const formValid = validateForm();
      if (!formValid) {
        return;
      }
      const universityData = { title: universityName, type: universityType?.value };

      setLoading(true);

      if (id) {
        // --- UPDATE EXISTING UNIVERSITY ---
        await updateUniversity(id, universityData);

        Swal.fire({
          icon: "success",
          title: "Success",
          text: "University details updated successfully",
        });
        navigate("/university", {
          state: {
            page: location.state?.page || 1,
            searchTerm: location.state?.searchTerm || "",
          },
        });
      } else {
        // --- CREATE NEW UNIVERSITY ---
        const response = await addUniversity(universityData);
        if (response.data.success) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "University added successfully",
          });
          navigate("/university", {
            state: {
              page: location.state?.page || 1,
              searchTerm: location.state?.searchTerm || "",
            },
          });
        }
      }
    } catch (err) {
      console.error("Error in handleSave:", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.message || "Operation failed",
      });
    } finally {
      // hide loader
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div id="preloader">
        <div className="sk-three-bounce">
          <div className="sk-child sk-bounce1"></div>
          <div className="sk-child sk-bounce2"></div>
          <div className="sk-child sk-bounce3"></div>
        </div>
      </div>
    );
  }
  return (
    <>
      <PageTitle
        activeMenu={`${id ? "Edit University" : "Add University"}`}
        motherMenu="University"
        activeMenuLink={`${id ? `edit-university/${id}` : "add-university"}`}
        motherMenuLink={"university"}
      />
      <div className="row">
        <div className="col-xl-12">
          <div className="row">
            <div className="col-xl-12 col-xxl-12">
              <div className="card h-auto">
                <div className="card-body">
                  <form>
                    <div
                      onClick={() => {
                        navigate("/university", {
                          state: {
                            page: location.state?.page || 1,
                            searchTerm: location.state?.searchTerm || "",
                          },
                        });
                      }}
                      className="mb-4 d-flex align-items-center flex-wrap gap-2 cursor-pointer w-max"
                    >
                      <i className="fas fa-chevron-left"></i>
                      <div>University List</div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Title</label>
                      <input
                        type="text"
                        className="form-control w-full"
                        placeholder="Title"
                        value={universityName}
                        onChange={(e) => setUniversityName(e.target.value)}
                      />
                      {errors.universityName && (
                        <div className="text-danger mt-1">
                          {errors.universityName}
                        </div>
                      )}{" "}
                    </div>
                    <div className="mb-3">
                      <label className="form-label">University Type</label>
                      <Select
                        value={universityType}
                        onChange={(value) =>
                          setUniversityType(value)
                        }
                        options={statusOptions}
                        placeholder="Select university type"
                        closeMenuOnSelect={true}
                        menuPlacement="auto"
                        menuPosition="fixed"
                      />
                    </div>
                  </form>
                </div>
              </div>
              <hr style={{ margin: "0px" }} className="mx-2" />
              <div className="card-footer border-0 text-end py-3 d-flex justify-content-end gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => {
                    navigate("/university", {
                      state: {
                        page: location.state?.page || 1,
                        searchTerm: location.state?.searchTerm || "",
                      },
                    })
                  }}
                >
                  Back
                </Button>

                {/* Save / Update Button */}
                <Button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={handleSave}
                >
                  {id ? "Update" : "Save"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddUniversity;
